/**
 * Email Templates Page
 *
 * The transactional emails Manifold sends on behalf of the selected clinic:
 * invoice receipts, subscription lifecycle notices, card-expiry warnings and
 * the four dunning emails. Each is a self-contained HTML document stored per
 * tenant, rendered with Jinja at send time.
 *
 * Read-only for everyone; super_admin / clinic_admin can swap the HTML
 * (the backend gates the write regardless of what this page renders).
 *
 * Two things about this editor differ from the catalog and are worth knowing
 * before using it:
 *
 *   Nothing is staged. Pricing edits queue as draft changes and ship with a
 *   release. Templates have no such path — a save lands on the next email the
 *   tenant sends. The save button says so.
 *
 *   Nothing is versioned. Manifold stores one row per template with no history,
 *   so there is no revert-after-save. "Discard changes" only reaches back to
 *   what was loaded, which is why the preview is prominent and the save is not.
 *
 * The preview is rendered by our own backend against a sample context, not by
 * Manifold — Manifold has no preview endpoint. Same reason it is also the save
 * gate: a template that will not render does not merely lose an email, it
 * raises inside Manifold's payment and webhook paths.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { manifoldAPI, emailPreviewUrl } from '../services/api'
import {
  Card, CardContent,
  Button, Badge,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, AlertTriangle, Mail, Save, Undo2,
  CheckCircle2, Eye, Code, Lock,
} from 'lucide-react'

/**
 * Display grouping for the template list.
 *
 * Manifold has no notion of a template group — the keys are flat filenames —
 * so this exists purely so a list of twelve does not read as one undifferentiated
 * block. A key that matches no group falls into "Other" rather than disappearing,
 * which is what makes a template added on the Manifold side still show up here.
 */
const GROUPS = [
  {
    name: 'Invoices',
    // invoice_paid_first only exists once a tenant creates it — Manifold falls
    // back to invoice_paid otherwise — so it is usually absent from the list.
    keys: ['invoice_paid', 'invoice_paid_first', 'invoice_refunded'],
  },
  {
    name: 'Subscriptions',
    keys: [
      'subscription_created',
      'subscription_changed',
      'subscription_renew',
      'subscription_cancelled',
    ],
  },
  {
    name: 'Cards',
    keys: ['card_to_expire', 'card_expired'],
  },
  {
    name: 'Dunning',
    keys: [
      'dunning_payment_failed_initial',
      'dunning_payment_retry',
      'dunning_final_warning',
      'subscription_paused_dunning',
    ],
  },
]

/** Human label for a template key, falling back to a de-underscored key. */
const LABELS = {
  invoice_paid: 'Invoice paid',
  invoice_paid_first: 'Invoice paid (first)',
  invoice_refunded: 'Invoice refunded',
  subscription_created: 'Subscription created',
  subscription_changed: 'Subscription changed',
  subscription_renew: 'Renewal reminder',
  subscription_cancelled: 'Subscription cancelled',
  card_to_expire: 'Card about to expire',
  card_expired: 'Card expired',
  dunning_payment_failed_initial: 'Payment failed (first)',
  dunning_payment_retry: 'Payment retry',
  dunning_final_warning: 'Final warning',
  subscription_paused_dunning: 'Subscription paused',
}

function labelFor(key) {
  return LABELS[key] || String(key).replace(/_/g, ' ')
}

/**
 * Whether a subject still has placeholders in it.
 *
 * Subjects come back as Jinja source, not final text — Manifold renders them
 * with the email's context at send time, so `{{brand_name}}` becomes the
 * tenant's sender name and `{{renewal_date}}` the actual date. Showing the
 * source is the honest option: rendering it here would mean guessing at a
 * brand this page has no way to know, which is the exact mistake the old
 * hardcoded subject list made.
 */
function hasPlaceholders(subject) {
  return typeof subject === 'string' && subject.includes('{{')
}

function formatUpdated(ms) {
  if (!ms) return 'never edited'
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return 'never edited'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The rendered email.
 *
 * An iframe rather than dangerouslySetInnerHTML, and not only for safety: these
 * are whole HTML documents with their own <style> blocks, and injecting one
 * into the page would let its styles escape into the dashboard around it.
 *
 * Loaded by URL rather than as `srcdoc`, which is what makes the logos appear.
 * A srcdoc document inherits the embedding page's CSP, and ours permits images
 * only from 'self', data:, Google and gstatic — so every template rendered with
 * its layout intact and its S3-hosted logo missing. Fetched from its own URL,
 * the document is governed by that response's policy instead.
 *
 * `sandbox` with no allow-scripts still means nothing in the template can run,
 * which is the right default for markup an operator is about to send.
 */
function PreviewFrame({ previewId }) {
  const src = emailPreviewUrl(previewId)
  if (!src) return null
  return (
    <iframe
      title="Email preview"
      sandbox=""
      src={src}
      className="w-full h-[640px] bg-white rounded-b-lg border-0"
    />
  )
}

function TemplateListItem({ template, selected, dirty, onSelect }) {
  const off = template.enabled === false
  return (
    <button
      type="button"
      onClick={() => onSelect(template.template_key)}
      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
        selected
          ? 'bg-primary/10 text-foreground'
          : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground'
      } ${off ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-medium truncate ${off ? 'line-through' : ''}`}>
          {labelFor(template.template_key)}
        </span>
        {dirty && (
          <span
            className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0"
            title="Unsaved changes"
          />
        )}
      </div>
      <div className="text-xs text-muted-foreground/70 truncate">
        {formatUpdated(template.updated_at)}
        {template.updated_by && template.updated_by !== 'seed' && ` · ${template.updated_by}`}
      </div>
    </button>
  )
}

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([])
  const [canEdit, setCanEdit] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedKey, setSelectedKey] = useState(null)
  const [loadingTemplate, setLoadingTemplate] = useState(false)
  // The html as Manifold currently has it, and the html in the editor. Keeping
  // both is what makes "dirty" and "discard" answerable without a refetch.
  const [savedHtml, setSavedHtml] = useState('')
  const [draftHtml, setDraftHtml] = useState('')
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const [tab, setTab] = useState('preview')
  // Disabled templates are hidden, not dropped. Hiding them is the point —
  // a tenant that switched a receipt off should not have to scroll past it —
  // but making them unreachable would leave no way to see one exists, let
  // alone that its body is still sitting there ready to be switched back on.
  const [showDisabled, setShowDisabled] = useState(false)

  const fetchList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await manifoldAPI.getEmailTemplates()
      const data = res.data?.data || res.data
      setTemplates(data?.templates || [])
      setCanEdit(!!data?.can_edit)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchList() }, [fetchList])

  // Open the first template once the list arrives, so the page does not land on
  // an empty right-hand pane.
  useEffect(() => {
    if (selectedKey || templates.length === 0) return
    // Prefer one that is switched on: landing on a disabled template would
    // open the page on an email the tenant does not send.
    const first = templates.find((t) => t.enabled !== false) || templates[0]
    setSelectedKey(first.template_key)
  }, [templates, selectedKey])

  const loadTemplate = useCallback(async (key) => {
    setLoadingTemplate(true)
    setSaveError(null)
    setSavedAt(null)
    try {
      const res = await manifoldAPI.getEmailTemplate(key)
      const data = res.data?.data || res.data
      setSavedHtml(data?.html || '')
      setDraftHtml(data?.html || '')
      setPreview(data?.preview || null)
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || err.message)
      setSavedHtml('')
      setDraftHtml('')
      setPreview(null)
    } finally {
      setLoadingTemplate(false)
    }
  }, [])

  useEffect(() => {
    if (selectedKey) loadTemplate(selectedKey)
  }, [selectedKey, loadTemplate])

  const dirty = draftHtml !== savedHtml

  // The tenant's real preview line for the open template, read from the list
  // rather than the editor because it is not part of the html being edited.
  const selectedPreheader = templates
    .find((t) => t.template_key === selectedKey)?.effective_preheader ?? null

  // Re-render the preview as the operator types, debounced. The request is
  // cheap (our backend, no Manifold round trip) but a render per keystroke on a
  // 700-line document is still wasted work, and a preview that flickers on
  // every character is harder to read than one that settles.
  const previewTimer = useRef(null)
  const previewSeq = useRef(0)

  useEffect(() => {
    if (!selectedKey || !dirty) return
    clearTimeout(previewTimer.current)
    previewTimer.current = setTimeout(async () => {
      const seq = ++previewSeq.current
      setPreviewing(true)
      try {
        const res = await manifoldAPI.previewEmailTemplate(selectedKey, draftHtml, selectedPreheader)
        // Drop a response that a later edit has already superseded, so the
        // pane cannot end up showing an older render than the editor.
        if (seq !== previewSeq.current) return
        setPreview(res.data?.data || res.data)
      } catch (err) {
        if (seq !== previewSeq.current) return
        setPreview({
          ok: false,
          html: null,
          error: err.response?.data?.error?.message || err.message,
          unknownVariables: [],
          variables: [],
        })
      } finally {
        if (seq === previewSeq.current) setPreviewing(false)
      }
    }, 400)

    return () => clearTimeout(previewTimer.current)
  }, [draftHtml, dirty, selectedKey, selectedPreheader])

  const handleSave = async () => {
    if (!selectedKey || !dirty) return
    setSaving(true)
    setSaveError(null)
    try {
      const res = await manifoldAPI.saveEmailTemplate(selectedKey, draftHtml)
      const data = res.data?.data || res.data
      setSavedHtml(draftHtml)
      setSavedAt(data?.updated_at || Date.now())
      // Refresh the list so the row's "last edited" stops lying.
      fetchList()
    } catch (err) {
      const body = err.response?.data?.error
      setSaveError(body?.details ? `${body.message} ${body.details}` : (body?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setDraftHtml(savedHtml)
    setSaveError(null)
  }

  // Group for display, then append anything ungrouped so a template added on
  // the Manifold side is still reachable.
  const disabledCount = templates.filter((t) => t.enabled === false).length

  const grouped = useMemo(() => {
    const visible = showDisabled ? templates : templates.filter((t) => t.enabled !== false)
    const byKey = new Map(visible.map((t) => [t.template_key, t]))
    const out = []
    const claimed = new Set()

    for (const group of GROUPS) {
      const rows = group.keys.map((k) => byKey.get(k)).filter(Boolean)
      rows.forEach((r) => claimed.add(r.template_key))
      if (rows.length) out.push({ name: group.name, rows })
    }

    const rest = visible.filter((t) => !claimed.has(t.template_key))
    if (rest.length) out.push({ name: 'Other', rows: rest })

    return out
  }, [templates, showDisabled])

  const selected = templates.find((t) => t.template_key === selectedKey)

  // Manifold is the only source for these. A Manifold predating
  // MDExam/payments#322 sends none, in which case say so rather than showing a
  // guess — this page used to carry its own copy of the subject literals and
  // they went stale the moment that shipped.
  const subject = selected?.effective_subject || null

  // Whether this tenant has overridden the built-in subject.
  const subjectIsCustom = !!selected?.subject

  const preheader = selected?.effective_preheader || null
  const preheaderIsCustom = !!selected?.preheader
  const isDisabled = selected?.enabled === false

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading email templates...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
        <Button variant="outline" onClick={fetchList}>
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Email Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {templates.length - disabledCount} transactional{' '}
            {templates.length - disabledCount === 1 ? 'email' : 'emails'} Manifold sends for this clinic
            {disabledCount > 0 && `, ${disabledCount} switched off`}
            {canEdit
              ? <span className="ml-2 text-amber-600 dark:text-amber-400">· edits go live immediately, with no release</span>
              : <span className="ml-2">· read-only for your role</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchList}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
        {/* Template list */}
        <Card>
          <CardContent className="p-3 space-y-4">
            {grouped.map((group) => (
              <div key={group.name}>
                <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">
                  {group.name}
                </div>
                <div className="space-y-0.5">
                  {group.rows.map((t) => (
                    <TemplateListItem
                      key={t.template_key}
                      template={t}
                      selected={t.template_key === selectedKey}
                      dirty={t.template_key === selectedKey && dirty}
                      onSelect={setSelectedKey}
                    />
                  ))}
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <p className="px-3 py-6 text-sm text-muted-foreground">
                This tenant has no seeded email templates.
              </p>
            )}

            {disabledCount > 0 && (
              <button
                type="button"
                onClick={() => setShowDisabled((v) => !v)}
                className="w-full text-left px-3 py-2 text-xs text-muted-foreground hover:text-foreground border-t border-border mt-2 pt-3"
              >
                {showDisabled
                  ? `Hide ${disabledCount} switched off`
                  : `${disabledCount} switched off — show`}
              </button>
            )}
          </CardContent>
        </Card>

        {/* Editor + preview */}
        <div className="space-y-4 min-w-0">
          {loadingTemplate ? (
            <Card>
              <CardContent className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading template...</span>
              </CardContent>
            </Card>
          ) : !selectedKey ? null : (
            <>
              {/* Subject — stored per tenant, but not editable from here yet */}
              <Card>
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <h2 className="font-semibold text-foreground truncate">
                          {labelFor(selectedKey)}
                        </h2>
                        <code className="text-xs text-muted-foreground/70 font-mono truncate">
                          {selectedKey}
                        </code>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-sm min-w-0">
                        <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground flex-shrink-0">Subject:</span>
                        {subject ? (
                          <>
                            <span className="text-foreground font-mono text-xs truncate">{subject}</span>
                            {subjectIsCustom && (
                              <Badge variant="outline" className="flex-shrink-0 text-xs">custom</Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground/60 italic">
                            not reported by Manifold
                          </span>
                        )}
                      </div>
                      {/* Preview text: the line an inbox shows beside the
                          subject. Stored on the same row and rendered with the
                          same context, but printed by a hidden block in the
                          body rather than being part of the visible copy —
                          so without this it is invisible in both panes. */}
                      <div className="mt-1.5 flex items-center gap-2 text-sm min-w-0">
                        <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground flex-shrink-0">Preview text:</span>
                        {preheader ? (
                          <>
                            <span className="text-foreground font-mono text-xs truncate">{preheader}</span>
                            {preheaderIsCustom && (
                              <Badge variant="outline" className="flex-shrink-0 text-xs">custom</Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground/60 italic">
                            not reported by Manifold
                          </span>
                        )}
                      </div>
                      {(hasPlaceholders(subject) || hasPlaceholders(preheader)) && (
                        <div className="mt-1 ml-5 text-xs text-muted-foreground/70">
                          Placeholders are filled at send time — the brand comes from this tenant's
                          sender name in Manifold.
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-muted-foreground">
                        Last edited {formatUpdated(selected?.updated_at)}
                      </div>
                      {selected?.updated_by && (
                        <div className="text-xs text-muted-foreground/70">by {selected.updated_by}</div>
                      )}
                    </div>
                  </div>

                  {/* The variables a template may reference. Without this the
                      only way to know is reading Manifold's email_utils.py. */}
                  {preview?.variables?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground mr-1">Available variables:</span>
                      {preview.variables.map((v) => (
                        <code
                          key={v}
                          className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                        >
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Switched off. Worth stating loudly on the template itself:
                  the row is hidden from the list by default, so the only way
                  to be looking at this one is to have gone looking — and an
                  edit here changes copy nobody currently receives. */}
              {isDisabled && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-border bg-muted/50">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <span className="font-medium text-foreground">This email is switched off.</span>
                    <span className="text-muted-foreground">
                      {' '}Manifold will not send it for this tenant. Editing the body here is
                      allowed and changes nothing until it is switched back on, which is not
                      something this page can do.
                    </span>
                  </div>
                </div>
              )}

              {/* Render problems */}
              {preview && !preview.ok && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-destructive/40 bg-destructive/5">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="text-sm min-w-0">
                    <div className="font-medium text-destructive">This template does not render</div>
                    <div className="text-muted-foreground font-mono text-xs mt-1 break-words">
                      {preview.error}
                    </div>
                    <div className="text-muted-foreground text-xs mt-1">
                      It cannot be saved while it fails — and if it is already live, the emails using
                      it are failing now.
                    </div>
                  </div>
                </div>
              )}

              {preview?.ok && preview.unknownVariables?.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900 dark:text-amber-100">
                    Renders, but reads {preview.unknownVariables.length === 1 ? 'a variable' : 'variables'} nothing supplies:{' '}
                    {preview.unknownVariables.map((v) => (
                      <code key={v} className="font-mono text-xs mx-0.5">{`{{${v}}}`}</code>
                    ))}
                    — {preview.unknownVariables.length === 1 ? 'it' : 'they'} will send as blank.
                  </div>
                </div>
              )}

              {saveError && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-destructive/40 bg-destructive/5">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-destructive break-words">{saveError}</span>
                </div>
              )}

              <Card className="overflow-hidden">
                <Tabs value={tab} onValueChange={setTab}>
                  <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-border">
                    <TabsList>
                      <TabsTrigger value="preview">
                        <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
                      </TabsTrigger>
                      <TabsTrigger value="html">
                        <Code className="h-3.5 w-3.5 mr-1.5" /> HTML
                      </TabsTrigger>
                    </TabsList>

                    <div className="flex items-center gap-2">
                      {previewing && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Loader2 className="h-3 w-3 animate-spin" /> rendering
                        </span>
                      )}
                      {!dirty && savedAt && (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Saved
                        </span>
                      )}
                      {dirty && (
                        <>
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Unsaved
                          </Badge>
                          <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={saving}>
                            <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Discard
                          </Button>
                        </>
                      )}
                      {canEdit && (
                        <Button
                          size="sm"
                          onClick={handleSave}
                          disabled={!dirty || saving || (preview && !preview.ok)}
                          title={
                            preview && !preview.ok
                              ? 'Fix the render error before saving'
                              : 'Saves to Manifold and applies to the next email sent'
                          }
                        >
                          {saving
                            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            : <Save className="h-3.5 w-3.5 mr-1.5" />}
                          Save &amp; go live
                        </Button>
                      )}
                    </div>
                  </div>

                  <TabsContent value="preview" className="m-0">
                    {preview?.ok
                      ? <PreviewFrame previewId={preview.preview_id} />
                      : (
                        <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
                          No preview — the template does not render.
                        </div>
                      )}
                  </TabsContent>

                  <TabsContent value="html" className="m-0">
                    <textarea
                      value={draftHtml}
                      onChange={(e) => setDraftHtml(e.target.value)}
                      readOnly={!canEdit}
                      spellCheck={false}
                      className="w-full h-[640px] p-4 font-mono text-xs leading-relaxed bg-background text-foreground border-0 outline-none resize-none disabled:opacity-60"
                      placeholder="Template HTML"
                    />
                  </TabsContent>
                </Tabs>
              </Card>

              <p className="text-xs text-muted-foreground">
                Preview renders against sample data using a Jinja-compatible engine, so it is a
                strong check and not a guarantee — Manifold renders the real thing with Jinja2.
                Manifold keeps no previous versions, so a save cannot be undone from here.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
